/**
 * Registrar entrega, troca ou devolução de EPI a um funcionário.
 *
 * Funcionário e catálogo de EPI são listas acessórias com permissão própria (`employee.read`,
 * `epi.read`): passam por `useListaPermitida`, que diz por que o campo ficou vazio em vez de
 * mostrar erro. A busca de funcionário vai ao servidor conforme se digita — a empresa pode ter
 * milhares.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { PageShell } from '../../../components';
import { CampoDeEpi } from './CampoDeEpi';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import employeeService from '../../../services/employeeService';
import entregaDeEpiService from '../../../services/entregaDeEpiService';
import { EmployeeStatus, type Employee } from '../../../types/employee';
import type { Epi } from '../../../types/epi';
import { ROTULO_DO_TIPO, TIPOS_DE_ENTREGA, type TipoDeEntregaDeEpi } from '../../../types/entregaDeEpi';

interface Linha {
  chave: number;
  epi: Epi | null;
  quantidade: string;
  tamanho: string;
}

function hoje(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

function caVencido(epi: Epi, data: string): boolean {
  return Boolean(epi.fimValidade) && epi.fimValidade.slice(0, 10) < data;
}

let proximaChave = 1;
const novaLinha = (): Linha => ({ chave: proximaChave++, epi: null, quantidade: '1', tamanho: '' });

export const EntregaDeEpiFormPage: React.FC = () => {
  const navigate = useNavigate();

  const [funcionario, setFuncionario] = useState<Employee | null>(null);
  const [busca, setBusca] = useState('');
  const buscaAtual = useRef('');
  const [tipo, setTipo] = useState<TipoDeEntregaDeEpi>('entrega');
  const [entregueEm, setEntregueEm] = useState(hoje());
  const [observacao, setObservacao] = useState('');
  const [linhas, setLinhas] = useState<Linha[]>([novaLinha()]);
  const [enviar, setEnviar] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregarFuncionarios = useCallback(async (): Promise<Employee[]> => {
    const resposta = await employeeService.getEmployees({
      page: 1,
      limit: 20,
      filters: { search: buscaAtual.current || undefined, status: EmployeeStatus.ATIVO },
    });
    return resposta.data;
  }, []);
  const funcionarios = useListaPermitida('employee.read', 'Funcionário', carregarFuncionarios);


  // A busca de funcionário espera a pessoa parar de digitar antes de ir ao servidor.
  const { recarregar: recarregarFuncionarios } = funcionarios;
  useEffect(() => {
    if (busca === buscaAtual.current) return;
    const espera = window.setTimeout(() => {
      buscaAtual.current = busca;
      recarregarFuncionarios();
    }, 350);
    return () => window.clearTimeout(espera);
  }, [busca, recarregarFuncionarios]);

  const atualizarLinha = (chave: number, mudanca: Partial<Linha>): void => {
    setLinhas((atual) => atual.map((l) => (l.chave === chave ? { ...l, ...mudanca } : l)));
  };

  const semContato = funcionario !== null && !funcionario.whatsapp && !funcionario.email;
  const linhasValidas = linhas.filter((l) => l.epi !== null);
  const vencidos = tipo === 'devolucao' ? [] : linhasValidas.filter((l) => l.epi && caVencido(l.epi, entregueEm));
  const podeSalvar =
    funcionario !== null &&
    linhasValidas.length > 0 &&
    linhasValidas.every((l) => Number.isInteger(Number(l.quantidade)) && Number(l.quantidade) >= 1) &&
    vencidos.length === 0 &&
    !(enviar && semContato) &&
    !salvando;

  const salvar = async (): Promise<void> => {
    if (!funcionario) return;
    setSalvando(true);
    setErro(null);
    try {
      const entrega = await entregaDeEpiService.registrar({
        employeeId: funcionario.id,
        tipo,
        entregueEm,
        observacao,
        itens: linhasValidas.map((l) => ({ epiId: l.epi?.id ?? 0, quantidade: Number(l.quantidade), tamanho: l.tamanho })),
        enviarParaAssinatura: enviar,
      });
      navigate(`/epis/entregas/${entrega.id}`);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível registrar a entrega.');
      setSalvando(false);
    }
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'EPIs', path: '/epis' },
        { label: 'Entregas', path: '/epis/entregas' },
        { label: 'Registrar' },
      ]}
      title="Registrar entrega de EPI"
      subtitle="Gera a ficha NR-6 com o C.A. de cada EPI e, se quiser, manda para o funcionário assinar pelo celular"
    >
      <Stack spacing={3} sx={{ maxWidth: 880 }}>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            <Autocomplete
              options={funcionarios.itens as Employee[]}
              value={funcionario}
              onChange={(_, valor) => setFuncionario(valor)}
              inputValue={busca}
              onInputChange={(_, valor) => setBusca(valor)}
              filterOptions={(opcoes) => opcoes}
              getOptionLabel={(f) => f.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              loading={funcionarios.carregando}
              disabled={!funcionarios.permitido}
              noOptionsText={busca ? 'Nenhum funcionário ativo com esse nome nesta empresa' : 'Digite o nome ou o CPF'}
              renderOption={(props, f) => (
                <li {...props} key={f.id}>
                  <Box>
                    <Typography variant="body2">{f.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[f.employeeCode, f.cpf].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Funcionário"
                  required
                  helperText={funcionarios.bloqueio ?? funcionarios.erro}
                  error={Boolean(funcionarios.erro)}
                />
              )}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel id="tipo-entrega">Tipo</InputLabel>
                <Select labelId="tipo-entrega" label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoDeEntregaDeEpi)}>
                  {TIPOS_DE_ENTREGA.map((t) => (
                    <MenuItem key={t} value={t}>
                      {ROTULO_DO_TIPO[t]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Data"
                type="date"
                value={entregueEm}
                onChange={(e) => setEntregueEm(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: hoje() }}
              />
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            EPIs
          </Typography>
          <Stack spacing={1.5}>
            {linhas.map((linha) => (
              <Stack key={linha.chave} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                <CampoDeEpi
                  valor={linha.epi}
                  onChange={(valor) => atualizarLinha(linha.chave, { epi: valor, tamanho: valor?.tamanho ?? linha.tamanho })}
                  conferirValidadeEm={tipo === 'devolucao' ? null : entregueEm}
                />
                <TextField
                  label="Qtd."
                  size="small"
                  type="number"
                  value={linha.quantidade}
                  onChange={(e) => atualizarLinha(linha.chave, { quantidade: e.target.value })}
                  inputProps={{ min: 1, max: 999 }}
                  sx={{ width: { sm: 90 } }}
                />
                <TextField
                  label="Tamanho"
                  size="small"
                  value={linha.tamanho}
                  onChange={(e) => atualizarLinha(linha.chave, { tamanho: e.target.value })}
                  inputProps={{ maxLength: 40 }}
                  sx={{ width: { sm: 120 } }}
                />
                <Tooltip title="Tirar este EPI">
                  <span>
                    <IconButton
                      aria-label="Tirar este EPI"
                      onClick={() => setLinhas((atual) => atual.filter((l) => l.chave !== linha.chave))}
                      disabled={linhas.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
          <Button startIcon={<AddIcon />} onClick={() => setLinhas((atual) => [...atual, novaLinha()])} sx={{ mt: 1.5 }}>
            Mais um EPI
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            <TextField
              label="Observação (opcional)"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              multiline
              minRows={2}
              inputProps={{ maxLength: 2000 }}
            />
            <FormControlLabel
              control={<Checkbox checked={enviar} onChange={(e) => setEnviar(e.target.checked)} />}
              label="Enviar a ficha para o funcionário assinar pelo celular (WhatsApp ou e-mail)"
            />
            {enviar && semContato && (
              <Alert severity="warning">
                {funcionario?.name} não tem WhatsApp nem e-mail no cadastro. Complete o cadastro ou desmarque o envio e
                colha a assinatura na ficha impressa.
              </Alert>
            )}
            {!enviar && (
              <Typography variant="body2" color="text.secondary">
                A ficha sai com linha para assinatura no papel.
              </Typography>
            )}
          </Stack>
        </Paper>

        {erro && <Alert severity="error">{erro}</Alert>}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={() => navigate('/epis/entregas')}>Cancelar</Button>
          <Button variant="contained" onClick={() => void salvar()} disabled={!podeSalvar}>
            {salvando ? 'Registrando…' : enviar ? 'Registrar e enviar para assinatura' : 'Registrar entrega'}
          </Button>
        </Stack>
      </Stack>
    </PageShell>
  );
};

export default EntregaDeEpiFormPage;
